import * as d3 from "d3";

class profileHeatmap {
  // Renders a shimmer skeleton heatmap using the same layout parameters as heatmaps().
  // Call this immediately on cell click so the grid stays visible while the API loads.
  shimmerHeatmap(
    datass,
    colNum = 2,
    myCellSize = 28,
    myCellHeight = 28,
    margin = { top: 8, right: 5, bottom: 20, left: 2 },
    widthVal = 90,
    showHeading = true,
    monthGap = 6,
    perRowCell = 6,
    cellGap = 1.8,
    x_axis_cellGap = 1.8,
    y_axis_cellGap = 1.8
  ) {
    const data = Array.isArray(datass) ? datass : [];
    const container = d3.select("#heatmap2");
    if (container.empty() || !data.length) return;

    container.html("");
    d3.selectAll(".tooltipHeatmap").remove();

    const width = widthVal - margin.left - margin.right;
    const cellSize = myCellSize;

    const getUniqueMonths = (rows) => {
      const unique = [];
      rows.forEach((d) => {
        const key = `${d.year}-${d.month}`;
        if (!unique.includes(key)) unique.push(key);
      });
      return unique;
    };

    const uniqueMonths = getUniqueMonths(data).slice(0, colNum);
    const monthRowCount = Math.max(1, Math.ceil(uniqueMonths.length / colNum));
    const getMonthDaysCount = (monthKey) => {
      const [year, month] = monthKey.split("-").map(Number);
      return new Date(year, month, 0).getDate();
    };
    const maxDaysInVisibleMonths = 31;
    const rowsPerMonth = Math.max(1, Math.ceil(maxDaysInVisibleMonths / perRowCell));
    const monthGridHeight = rowsPerMonth * cellSize + (rowsPerMonth - 1) * y_axis_cellGap;
    const monthTitleHeight = showHeading ? 8 : 0;
    const monthBlockHeight = monthTitleHeight + monthGridHeight;
    const interRowGap = cellGap * 5 + 50;
    const contentHeight = monthRowCount * monthBlockHeight + (monthRowCount - 1) * interRowGap;
    const height = Math.max(16, contentHeight);
    const svgHeight = height + margin.top + margin.bottom;

    const svgEl = container
      .append("svg")
      .attr("width", "100%")
      .style("height", "auto")
      .style("display", "block")
      .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${svgHeight}`)
      .attr("preserveAspectRatio", "xMidYMin meet");

    // Inject shimmer animation into SVG defs
    const defs = svgEl.append("defs");
    defs.html(`
      <linearGradient id="shimmerGrad" x1="-100%" y1="0%" x2="0%" y2="0%">
        <stop offset="0%" stop-color="#e8edf2" stop-opacity="1" />
        <stop offset="50%" stop-color="#f5f7fa" stop-opacity="1" />
        <stop offset="100%" stop-color="#e8edf2" stop-opacity="1" />
        <animate attributeName="x1" from="-100%" to="100%" dur="1.2s" repeatCount="indefinite" />
        <animate attributeName="x2" from="0%" to="200%" dur="1.2s" repeatCount="indefinite" />
      </linearGradient>
    `);

    const svg = svgEl
      .append("g")
      .attr("transform", `translate(${margin.left / 2}, ${margin.top})`);

    const monthBlockWidth = perRowCell * cellSize + (perRowCell - 1) * x_axis_cellGap;
    const contentWidth =
      uniqueMonths.length > 0
        ? uniqueMonths.length * monthBlockWidth + (uniqueMonths.length - 1) * monthGap
        : 0;
    const chartOffsetX = Math.max(0, (width - contentWidth) / 2);

    const monthGroup = svg
      .selectAll(".month")
      .data(uniqueMonths)
      .enter()
      .append("g")
      .attr("class", "month")
      .attr("transform", (d, i) => {
        const xOffset = chartOffsetX + (i % colNum) * (monthBlockWidth + monthGap);
        const yOffset = Math.floor(i / colNum) * (cellSize * perRowCell + cellGap * 5 + 50);
        return `translate(${xOffset}, ${yOffset})`;
      });

    if (showHeading) {
      const fontSize = Math.max(5.4, cellSize * 1.05);
      const skeletonWidth = cellSize * 3.5;
      const titleHeight = fontSize * 0.7;
      
      monthGroup
        .append("rect")
        .attr("class", "month-label-shimmer")
        .attr("x", monthBlockWidth / 2 - skeletonWidth / 2)
        .attr("y", -margin.top / 2 - titleHeight / 2)
        .attr("width", skeletonWidth)
        .attr("height", titleHeight)
        .attr("rx", titleHeight * 0.4)
        .attr("ry", titleHeight * 0.4)
        .attr("fill", "url(#shimmerGrad)");
    }

    monthGroup.each(function(monthYear) {
      const [year, month] = monthYear.split("-").map(Number);
      const monthDays = d3.timeDays(new Date(year, month - 1, 1), new Date(year, month, 1));

      d3.select(this)
        .selectAll(".day-cell-shimmer")
        .data(monthDays)
        .enter()
        .append("rect")
        .attr("class", "day-cell-shimmer")
        .attr("x", (d, i) => (i % perRowCell) * (cellSize + x_axis_cellGap))
        .attr("y", (d, i) => Math.floor(i / perRowCell) * (cellSize + y_axis_cellGap))
        .attr("width", cellSize)
        .attr("height", cellSize)
        .attr("rx", cellSize * 0.12)
        .attr("ry", cellSize * 0.12)
        .attr("fill", "url(#shimmerGrad)");
    });
  }

  // Overlays shimmer rects on top of every existing day-cell in #heatmap2 (mobile scroll use).
  // Does NOT replace the SVG — titles stay visible, only cells shimmer.
  // Call removeMobileShimmer() to remove the overlay.
  applyMobileScrollShimmer() {
    const svg = d3.select("#heatmap2 svg");
    if (svg.empty()) return;

    // Ensure shimmerGrad defs exist in this SVG
    let defs = svg.select("defs");
    if (defs.empty()) defs = svg.insert("defs", ":first-child");
    if (defs.select("#shimmerGradMob").empty()) {
      defs.html(defs.html() + `
        <linearGradient id="shimmerGradMob" x1="-100%" y1="0%" x2="0%" y2="0%">
          <stop offset="0%" stop-color="#e8edf2" stop-opacity="1" />
          <stop offset="50%" stop-color="#f5f7fa" stop-opacity="1" />
          <stop offset="100%" stop-color="#e8edf2" stop-opacity="1" />
          <animate attributeName="x1" from="-100%" to="100%" dur="1.2s" repeatCount="indefinite" />
          <animate attributeName="x2" from="0%" to="200%" dur="1.2s" repeatCount="indefinite" />
        </linearGradient>
      `);
    }

    // Place a shimmer rect on top of every existing day-cell rect / image
    // by reading their bounding boxes and adding an overlay rect in the same <g>.
    svg.selectAll(".day-cell").each(function() {
      const g = d3.select(this);
      // Remove any previous overlay to avoid stacking
      g.selectAll(".mob-shimmer-overlay").remove();
      // Get size from the existing rect child
      const existingRect = g.select("rect");
      if (existingRect.empty()) return;
      const w = parseFloat(existingRect.attr("width") || 0);
      const h = parseFloat(existingRect.attr("height") || w);
      const rx = parseFloat(existingRect.attr("rx") || 0);
      g.append("rect")
        .attr("class", "mob-shimmer-overlay")
        .attr("x", 0).attr("y", 0)
        .attr("width", w).attr("height", h)
        .attr("rx", rx).attr("ry", rx)
        .attr("fill", "url(#shimmerGradMob)")
        .style("pointer-events", "none");
    });
  }

  removeMobileScrollShimmer() {
    d3.selectAll("#heatmap2 .mob-shimmer-overlay").remove();
  }

  // Renders 7 months (6 visible + 1 incoming), translates the SVG <g> by one month-width,
  // then fires onDone() when the CSS transition ends so the caller can snap to a clean 6-month render.
  slideHeatmap(
    datass,         // 7 months of data (incoming + current 6), sorted oldest→newest
    direction,      // "left" (forward in time) or "right" (backward in time)
    colNum,
    myCellSize,
    margin,
    widthVal,
    monthGap,
    perRowCell,
    cellGap,
    x_axis_cellGap,
    y_axis_cellGap,
    activeIcon,
    inactiveIcon,
    onClickCell,
    selectedDateStr,
    onDone
  ) {
    const data = Array.isArray(datass) ? datass : [];
    const container = d3.select("#heatmap2");
    if (container.empty() || !data.length) { if (onDone) onDone(); return; }

    container.html("");
    d3.selectAll(".tooltipHeatmap").remove();

    const cellSize = myCellSize;
    const monthBlockWidth = perRowCell * cellSize + (perRowCell - 1) * x_axis_cellGap;
    const width = widthVal - margin.left - margin.right;

    // Build sorted unique month keys from the 7-month data
    const getUniqueMonths = (rows) => {
      const unique = [];
      rows.forEach((d) => {
        const key = `${d.year}-${String(d.month).padStart(2,"0")}`;
        if (!unique.includes(key)) unique.push(key);
      });
      return unique;
    };
    const allMonths = getUniqueMonths(data); // up to 7 months

    // Height calculation (based on all months, same as heatmaps)
    const getMonthDaysCount = (monthKey) => {
      const [y, m] = monthKey.split("-").map(Number);
      return new Date(y, m, 0).getDate();
    };
    const maxDays = 31;
    const rowsPerMonth = Math.max(1, Math.ceil(maxDays / perRowCell));
    const monthGridHeight = rowsPerMonth * cellSize + (rowsPerMonth - 1) * y_axis_cellGap;
    const monthTitleHeight = 8;
    const monthBlockHeight = monthTitleHeight + monthGridHeight;
    const height = Math.max(16, monthBlockHeight);
    const svgHeight = height + margin.top + margin.bottom;

    // totalCols = allMonths.length (7). The SVG viewBox is wide enough for all 7.
    const totalCols = allMonths.length;
    const totalContentWidth = totalCols * monthBlockWidth + (totalCols - 1) * monthGap;

    // For "left" slide: incoming month is at the RIGHT end (index 6), slide group left by one block
    // For "right" slide: incoming month is at the LEFT end (index 0), start offset = -1 block, slide to 0
    const unitPx = monthBlockWidth + monthGap; // one month-width in SVG user units

    const svgEl = container
      .append("svg")
      .attr("width", "100%")
      .style("height", "auto")
      .style("display", "block")
      .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${svgHeight}`)
      .attr("preserveAspectRatio", "xMidYMin meet");

    // Compute chartOffsetX based on the visible colNum, matching heatmaps()
    const contentWidthForVisible = colNum > 0
        ? colNum * monthBlockWidth + (colNum - 1) * monthGap
        : 0;
    const chartOffsetX = Math.max(0, (width - contentWidthForVisible) / 2);

    const clipId = "clip-slide-" + Math.random().toString(36).substr(2, 9);
    svgEl.append("defs").append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", margin.left / 2 + chartOffsetX)
      .attr("y", 0)
      .attr("width", contentWidthForVisible)
      .attr("height", svgHeight);

    const clippedWrapper = svgEl.append("g").attr("clip-path", `url(#${clipId})`);
    const g = clippedWrapper.append("g").attr("class", "hm-slide-group");

    // Starting translateX in SVG user-units:
    //   left  → group starts at 0 (first 6 months visible), will animate to -unitPx
    //   right → group starts at -unitPx (months 1-7 visible would be wrong; actually group starts shifted right by one unit)
    const startX = direction === "left" ? 0 : -unitPx;
    const endX   = direction === "left" ? -unitPx : 0;

    g.attr("transform", `translate(${margin.left / 2 + chartOffsetX + startX}, ${margin.top})`);

    // Render all months inside the group
    const monthGroup = g
      .selectAll(".month")
      .data(allMonths)
      .enter()
      .append("g")
      .attr("class", "month")
      .attr("transform", (d, i) => `translate(${i * unitPx}, 0)`);

    // Month labels
    monthGroup
      .append("text")
      .attr("class", "month-label")
      .attr("x", monthBlockWidth / 2)
      .attr("y", -margin.top / 2)
      .attr("text-anchor", "middle")
      .text((d) => {
        const [yr, mo] = d.split("-");
        return d3.timeFormat("%b, %Y")(new Date(Number(yr), Number(mo) - 1, 1));
      })
      .style("fill", "#3A3C40")
      .style("font-size", `${Math.max(5.4, cellSize * 1.05)}px`)
      .style("font-weight", "500")
      .style("font-family", "Montserrat");

    // Tooltip
    const tooltip = d3.select("body")
      .append("div")
      .attr("class", "tooltipHeatmap")
      .style("opacity", 0)
      .style("width", "auto").style("height", "auto")
      .style("padding", "8px 12px").style("border-radius", "8px")
      .style("background-color", "#D9D9D9").style("position", "absolute")
      .style("text-align", "center").style("font-family", "Montserrat")
      .style("font-size", "10px").style("font-weight", "400")
      .style("line-height", "12px").style("color", "#696969")
      .style("white-space", "nowrap").style("z-index", "9999")
      .style("pointer-events", "none");

    const getDatumByDate = (d) => {
      const dateStr = d3.timeFormat("%Y-%m-%d")(d);
      return data.find(e =>
        `${e.year}-${("0"+e.month).slice(-2)}-${("0"+e.day).slice(-2)}` === dateStr
      );
    };
    const hasActivity = (d) => { const dt = getDatumByDate(d); return !!(dt && dt.count > 0); };
    const isSelected = (d) => selectedDateStr && d3.timeFormat("%Y-%m-%d")(d) === selectedDateStr;
    const formatFullDate = d3.timeFormat("%b %d, %Y");
    const handleTooltip = (event, d) => {
      if (window.innerWidth <= 768) return;
      const dt = getDatumByDate(d);
      const count = dt ? dt.count : 0;
      const text = count > 1 ? `${count} Activities on ${formatFullDate(d)}`
        : count === 1 ? `1 Activity on ${formatFullDate(d)}`
        : `No Activities on ${formatFullDate(d)}`;
      const touch = event.type && event.type.startsWith("touch") ? event.touches[0] : event;
      tooltip.style("opacity",1).style("visibility","visible")
        .style("left",`${touch.pageX-25}px`).style("top",`${touch.pageY-35}px`)
        .html(`<strong>${text}</strong>`);
    };

    monthGroup.each(function(monthYear) {
      const [yr, mo] = monthYear.split("-").map(Number);
      const monthDays = d3.timeDays(new Date(yr, mo-1, 1), new Date(yr, mo, 1));
      const dayCell = d3.select(this).selectAll(".day-cell").data(monthDays).enter().append("g")
        .attr("class","day-cell")
        .attr("transform",(d,i) => `translate(${(i%perRowCell)*(cellSize+x_axis_cellGap)},${Math.floor(i/perRowCell)*(cellSize+y_axis_cellGap)})`);

      dayCell.append("image").attr("x",0).attr("y",0).attr("width",cellSize).attr("height",cellSize)
        .attr("href", d => hasActivity(d) ? activeIcon : inactiveIcon)
        .attr("xlink:href", d => hasActivity(d) ? activeIcon : inactiveIcon)
        .style("display", d => {
          if (isSelected(d) && hasActivity(d)) return "none";
          if (hasActivity(d)) return activeIcon ? "block" : "none";
          return inactiveIcon ? "block" : "none";
        }).style("pointer-events","none");

      dayCell.append("rect").attr("x",0).attr("y",0).attr("width",cellSize).attr("height",cellSize)
        .attr("rx",cellSize*0.12).attr("ry",cellSize*0.12)
        .attr("fill", d => hasActivity(d) ? "#F59E42" : "#F3F4F6")
        .attr("stroke", d => isSelected(d) && !hasActivity(d) ? "#20639B" : "none")
        .attr("stroke-width", d => isSelected(d) && !hasActivity(d) ? 0.5 : 0)
        .style("display", d => {
          if (isSelected(d) && hasActivity(d)) return "none";
          if (hasActivity(d)) return activeIcon ? "none" : "block";
          return inactiveIcon ? "none" : "block";
        }).style("pointer-events","none");

      const selActive = dayCell.append("g")
        .style("display", d => isSelected(d) && hasActivity(d) ? "block" : "none")
        .style("pointer-events","none");
      selActive.append("rect").attr("x",0).attr("y",0).attr("width",cellSize).attr("height",cellSize)
        .attr("rx",cellSize*0.12).attr("ry",cellSize*0.12).attr("fill","#20639B");
      selActive.append("circle").attr("cx",cellSize/2).attr("cy",cellSize/2).attr("r",cellSize*0.35).attr("fill","#FFFFFF");
      selActive.append("path")
        .attr("d",`M ${cellSize*0.3} ${cellSize*0.52} L ${cellSize*0.42} ${cellSize*0.65} L ${cellSize*0.7} ${cellSize*0.35}`)
        .attr("stroke","#20639B").attr("stroke-width",cellSize*0.1)
        .attr("stroke-linecap","round").attr("stroke-linejoin","round").attr("fill","none");

      const hitBox = dayCell.append("rect").attr("x",0).attr("y",0).attr("width",cellSize).attr("height",cellSize)
        .style("fill","transparent").style("pointer-events","all");
      hitBox.on("click",(event,d) => { if(onClickCell) onClickCell(d3.timeFormat("%Y-%m-%d")(d)); })
        .on("mouseenter",handleTooltip).on("mousemove",handleTooltip)
        .on("mouseout",() => tooltip.style("opacity",0).style("visibility","hidden"))
        .on("touchstart",(event, d) => {
          // Mark this touch so we can trigger onClickCell on touchend
          event.currentTarget.__touchStartDate = d3.timeFormat("%Y-%m-%d")(d);
          handleTooltip(event, d);
        })
        .on("touchend",(event) => {
          tooltip.style("opacity",0).style("visibility","hidden");
          // Fire onClickCell on touchend so it works even if the
          // browser swallows the click event due to layout shifts.
          const dateStr = event.currentTarget.__touchStartDate;
          if (dateStr && onClickCell) {
            event.currentTarget.__touchStartDate = null;
            onClickCell(dateStr);
          }
        });
    });

    // Use a generation token on the container so a stale transitionend from a previous
    // slide (whose SVG was destroyed by the next arrow press) never fires onDone.
    const container2 = document.getElementById("heatmap2");
    if (!container2) { if (onDone) onDone(); return; }
    const gen = (container2._slideGen = (container2._slideGen || 0) + 1);
    const myGen = gen;

    const gEl = g.node();
    gEl.style.transition = "none";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Abort if a newer slide has already replaced us
        if (container2._slideGen !== myGen) return;
        gEl.style.transition = "transform 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
        g.attr("transform", `translate(${margin.left / 2 + chartOffsetX + endX}, ${margin.top})`);
        let done = false;
        const onEnd = () => {
          if (done) return;
          done = true;
          gEl.removeEventListener("transitionend", onEnd);
          // Only fire onDone if we're still the active slide
          if (container2._slideGen !== myGen) return;
          tooltip.style("opacity", 0).style("visibility", "hidden");
          if (onDone) onDone();
        };
        gEl.addEventListener("transitionend", onEnd);
        setTimeout(onEnd, 480);
      });
    });
  }

  async heatmaps(
    datass,
    colNum = 2,
    myCellSize = 28,
    myCellHeight = 28,
    margin = { top: 8, right: 5, bottom: 20, left: 2 },
    widthVal = 90,
    showHeading = true,
    monthGap = 6,
    perRowCell = 6,
    cellGap = 1.8,
    x_axis_cellGap = 1.8,
    y_axis_cellGap = 1.8,
    activeIcon,
    inactiveIcon,
    onClickCell,
    selectedDateStr
  ) {
    const data = Array.isArray(datass) ? datass : [];
    const container = d3.select("#heatmap2");

    if (container.empty()) {
      return;
    }

    container.style("display", "block");

    container.html("");
    d3.selectAll(".tooltipHeatmap").remove();

    if (!data.length) {
      return;
    }

    const width = widthVal - margin.left - margin.right;
    const cellSize = myCellSize;

    const getUniqueMonths = (rows) => {
      const unique = [];
      rows.forEach((d) => {
        const key = `${d.year}-${d.month}`;
        if (!unique.includes(key)) {
          unique.push(key);
        }
      });
      return unique;
    };

    const uniqueMonths = getUniqueMonths(data).slice(0, colNum);
    const monthRowCount = Math.max(1, Math.ceil(uniqueMonths.length / colNum));
    const getMonthDaysCount = (monthKey) => {
      const [year, month] = monthKey.split("-").map(Number);
      return new Date(year, month, 0).getDate();
    };
    const maxDaysInVisibleMonths = 31;
    const rowsPerMonth = Math.max(1, Math.ceil(maxDaysInVisibleMonths / perRowCell));
    const monthGridHeight =
      rowsPerMonth * cellSize + (rowsPerMonth - 1) * y_axis_cellGap;
    const monthTitleHeight = showHeading ? 8 : 0;
    const monthBlockHeight = monthTitleHeight + monthGridHeight;
    const interRowGap = cellGap * 5 + 50;
    const contentHeight =
      monthRowCount * monthBlockHeight + (monthRowCount - 1) * interRowGap;
    const height = Math.max(16, contentHeight);
    const svgHeight = height + margin.top + margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", "100%")
      .style("height", "auto")
      .style("display", "block")
      .attr(
        "viewBox",
        `0 0 ${width + margin.left + margin.right} ${
          svgHeight
        }`
      )
      .attr("preserveAspectRatio", "xMidYMin meet")
      .append("g")
      .attr("transform", `translate(${margin.left / 2}, ${margin.top})`);
    const monthBlockWidth =
      perRowCell * cellSize + (perRowCell - 1) * x_axis_cellGap;
    const contentWidth =
      uniqueMonths.length > 0
        ? uniqueMonths.length * monthBlockWidth +
          (uniqueMonths.length - 1) * monthGap
        : 0;
    const chartOffsetX = Math.max(0, (width - contentWidth) / 2);

    const monthGroup = svg
      .selectAll(".month")
      .data(uniqueMonths)
      .enter()
      .append("g")
      .attr("class", "month")
      .attr("transform", (d, i) => {
        const xOffset =
          chartOffsetX + (i % colNum) * (monthBlockWidth + monthGap);
        const yOffset =
          Math.floor(i / colNum) * (cellSize * perRowCell + cellGap * 5 + 50);
        return `translate(${xOffset}, ${yOffset})`;
      });

    if (showHeading) {
      monthGroup
        .append("text")
        .attr("class", "month-label")
        .attr("x", monthBlockWidth / 2)
        .attr("y", -margin.top / 2)
        .attr("text-anchor", "middle")
        .text((d) => {
          const [year, month] = d.split("-");
          return d3.timeFormat("%b, %Y")(
            new Date(Number(year), Number(month) - 1, 1)
          );
        })
        .style("color", "#3A3C40")
        .style("font-size", `${Math.max(5.4, cellSize * 1.05)}px`)
        .style("font-weight", "500")
        .style("font-family", "Montserrat");
    }

    const tooltip = d3
      .select("body")
      .append("div")
      .style("opacity", 0)
      .attr("class", "tooltipHeatmap")
      .style("width", "auto")
      .style("height", "auto")
      .style("padding", "8px 12px")
      .style("border-radius", "8px")
      .style("background-color", "#D9D9D9")
      .style("position", "absolute")
      .style("text-align", "center")
      .style("font-family", "Montserrat")
      .style("font-size", "10px")
      .style("font-weight", "400")
      .style("line-height", "12px")
      .style("color", "#696969")
      .style("white-space", "nowrap")
      .style("z-index", "9999")
      .style("pointer-events", "none");

    const getDatumByDate = (d) => {
      const dateStr = d3.timeFormat("%Y-%m-%d")(d);
      return data.find(
        (e) =>
          `${e.year}-${("0" + e.month).slice(-2)}-${("0" + e.day).slice(
            -2
          )}` === dateStr
      );
    };

    const hasActivity = (d) => {
      const datum = getDatumByDate(d);
      return !!(datum && datum.count > 0);
    };

    const hasIcons = !!activeIcon && !!inactiveIcon;

    const formatFullDate = d3.timeFormat("%b %d, %Y");

    const handleTooltip = (event, d) => {
      if (window.innerWidth <= 768) return;
      const datum = getDatumByDate(d);
      const count = datum ? datum.count : 0;
      const text =
        count > 1
          ? `${count} Activities on ${formatFullDate(d)}`
          : count === 1
          ? `1 Activity on ${formatFullDate(d)}`
          : `No Activities on ${formatFullDate(d)}`;

      const touch =
        event.type && event.type.startsWith("touch") ? event.touches[0] : event;

      tooltip
        .style("opacity", 1)
        .style("visibility", "visible")
        .style("left", `${touch.pageX - 25}px`)
        .style("top", `${touch.pageY - 35}px`)
        .html(`<strong>${text}</strong>`);
    };

    monthGroup.each(function(monthYear) {
      const [year, month] = monthYear.split("-").map(Number);
      const monthDays = d3.timeDays(
        new Date(year, month - 1, 1),
        new Date(year, month, 1)
      );

      const dayCell = d3
        .select(this)
        .selectAll(".day-cell")
        .data(monthDays)
        .enter()
        .append("g")
        .attr("class", "day-cell")
        .attr("transform", (d, i) => {
          const x = (i % perRowCell) * (cellSize + x_axis_cellGap);
          const y = Math.floor(i / perRowCell) * (cellSize + y_axis_cellGap);
          return `translate(${x}, ${y})`;
        });

      const isSelected = (d) => {
        if (!selectedDateStr) return false;
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        return dateStr === selectedDateStr;
      };

      dayCell
        .append("image")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", cellSize)
        .attr("height", cellSize)
        .attr("href", (d) => (hasActivity(d) ? activeIcon : inactiveIcon))
        .attr("xlink:href", (d) =>
          hasActivity(d) ? activeIcon : inactiveIcon
        )
        .style("display", (d) => {
          if (isSelected(d) && hasActivity(d)) return "none";
          if (hasActivity(d)) return activeIcon ? "block" : "none";
          return inactiveIcon ? "block" : "none";
        })
        .style("pointer-events", "none");

      dayCell
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", cellSize)
        .attr("height", cellSize)
        .attr("rx", cellSize * 0.12)
        .attr("ry", cellSize * 0.12)
        .attr("fill", (d) => (hasActivity(d) ? "#F59E42" : "#F3F4F6"))
        .attr("stroke", (d) => (isSelected(d) && !hasActivity(d) ? "#20639B" : "none"))
        .attr("stroke-width", (d) => (isSelected(d) && !hasActivity(d) ? 0.5 : 0))
        .style("display", (d) => {
          if (isSelected(d) && hasActivity(d)) return "none";
          if (hasActivity(d)) return activeIcon ? "none" : "block";
          return inactiveIcon ? "none" : "block";
        })
        .style("pointer-events", "none");

      const selectedActiveGroup = dayCell
        .append("g")
        .style("display", (d) => (isSelected(d) && hasActivity(d) ? "block" : "none"))
        .style("pointer-events", "none");

      selectedActiveGroup
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", cellSize)
        .attr("height", cellSize)
        .attr("rx", cellSize * 0.12)
        .attr("ry", cellSize * 0.12)
        .attr("fill", "#20639B");

      selectedActiveGroup
        .append("circle")
        .attr("cx", cellSize / 2)
        .attr("cy", cellSize / 2)
        .attr("r", cellSize * 0.35)
        .attr("fill", "#FFFFFF");

      selectedActiveGroup
        .append("path")
        .attr("d", `M ${cellSize*0.3} ${cellSize*0.52} L ${cellSize*0.42} ${cellSize*0.65} L ${cellSize*0.7} ${cellSize*0.35}`)
        .attr("stroke", "#20639B")
        .attr("stroke-width", cellSize * 0.1)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("fill", "none");

      dayCell
        .append("text")
        .attr("x", cellSize / 2)
        .attr("y", cellSize / 2)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .style("font-family", "Arial, sans-serif")
        .style("font-weight", "900")
        .style("font-size", `${cellSize * 0.45}px`)
        .style("display", "none")
        .style("fill", "#BFC0C2")
        .text("x");

      const hitBox = dayCell
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", cellSize)
        .attr("height", cellSize)
        .style("fill", "transparent")
        .style("pointer-events", "all");

      hitBox
        .on("click", (event, d) => {
          if (onClickCell) {
            const dateStr = d3.timeFormat("%Y-%m-%d")(d);
            onClickCell(dateStr);
          }
        })
        .on("mouseenter", handleTooltip)
        .on("mousemove", handleTooltip)
        .on("mouseout", () =>
          tooltip.style("opacity", 0).style("visibility", "hidden")
        )
        .on("touchstart", (event, d) => {
          // Mark this touch so we can trigger onClickCell on touchend
          event.currentTarget.__touchStartDate = d3.timeFormat("%Y-%m-%d")(d);
          handleTooltip(event, d);
        })
        .on("touchend", (event) => {
          tooltip.style("opacity", 0).style("visibility", "hidden");
          // Fire onClickCell on touchend so it works even if the
          // browser swallows the click event due to layout shifts.
          const dateStr = event.currentTarget.__touchStartDate;
          if (dateStr && onClickCell) {
            event.currentTarget.__touchStartDate = null;
            onClickCell(dateStr);
          }
        });
    });
  }
}

export { profileHeatmap };
